import {
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonSection,
  IconSizeEnum,
  ModalDialog,
  ModalMessage,
  ModalMessageColorsEnum,
  PlusIcon,
  Typography,
} from '@acceso/design-system';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { TableWithDetailCard } from '@/components/TableWithDetailCard/TableWithDetailCard';
import { useNotificationStore } from '@/stores/notifications';
import { FormTypeEnum } from '@/types';
import { getParamsFromTableFilters } from '@/utils/table-filters';

import { useChannels } from '../api/get-channels';
import { usePatchChannel } from '../api/patch-channel';
import { usePostChannel } from '../api/post-channels';
import { BroadcastersDetailCard } from '../components/BroadcastersDetailCard';
import { BroadcastersDrawerForm } from '../components/BroadcastersDrawerForm';
import { BroadcastersTable } from '../components/BroadcastersTable';
import { useBroadcastersControllers } from '../hooks/useBroadcastersControllers';
import { useBroadcastersPermissions } from '../hooks/useBroadcastersPermissions';
import { useChannelFilters } from '../stores/channel-filters';
import { ChannelType } from '../types';

export default function BroadcastersPage() {
  const { t } = useTranslation();
  const { broadcastersDialog, broadcastersDrawer, handleDeleteBroadcasters } =
    useBroadcastersControllers();

  const { data: { data: allChannels = [] } = {} } = useChannels({
    params: { isParentChannel: true },
  });

  const { filters, paginationValues, clearFilters } = useChannelFilters();

  /**
   * Resets table filters on page unmount.
   */
  useEffect(() => {
    return () => {
      clearFilters();
    };
  }, [clearFilters]);

  const {
    data: { data: channels = [], total = 0 } = {},
    isFetching,
    isError,
  } = useChannels({
    params: getParamsFromTableFilters({
      filters: {
        ...filters,
        isParentChannel: false,
      },
      paginationValues,
    }),
  });
  const { mutate: createChannel, isPending: isCreatingChannel } = usePostChannel();
  const { mutate: updateChannel, isPending: isUpdatingChannel } = usePatchChannel();

  const { canCreate, canUpdate, canDuplicate, canDelete } = useBroadcastersPermissions();
  const { addErrorNotification } = useNotificationStore();

  const onCreate = () => {
    if (!canCreate) return;
    broadcastersDrawer.open({ type: FormTypeEnum.CREATE, data: null });
  };

  const onUpdate = (broadcaster: ChannelType) => {
    if (!canUpdate) return;
    broadcastersDrawer.open({ type: FormTypeEnum.UPDATE, data: broadcaster });
  };

  const onDuplicate = (broadcaster: ChannelType) => {
    if (!canDuplicate) return;
    broadcastersDrawer.open({ type: FormTypeEnum.DUPLICATE, data: broadcaster });
  };

  const onDelete = (broadcaster: ChannelType) => {
    if (!canDelete) return;
    broadcastersDialog.open(broadcaster);
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between py-size-8">
          <Typography
            fontSize={FontSizeEnum.Xl}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Dark10}
          >
            {t('Emisoras')}
          </Typography>

          {canCreate && (
            <IconButtonSection
              className="mr-size-16"
              onClick={onCreate}
              internalProps={{
                icon: {
                  size: IconSizeEnum.SM,
                  type: PlusIcon,
                },
                text: {
                  value: t('Nuevo'),
                  color: FontColorEnum.Default,
                },
              }}
            />
          )}
        </div>

        <TableWithDetailCard<ChannelType>
          isError={isError}
          renderTable={({ handleOpenSecondCard, data }) => (
            <BroadcastersTable
              items={channels}
              totalItems={total}
              activeItem={data}
              onUpdate={canUpdate ? onUpdate : undefined}
              onDuplicate={canDuplicate ? onDuplicate : undefined}
              onDelete={canDelete ? onDelete : undefined}
              onSelect={handleOpenSecondCard}
              isFetching={isFetching}
            />
          )}
          renderDetailCard={({ data, isSecondCardCollapsed, handleSecondCardCollapse }) => (
            <BroadcastersDetailCard
              broadcasters={data}
              isCollapsed={isSecondCardCollapsed}
              onCollapse={handleSecondCardCollapse}
              onUpdate={onUpdate}
            />
          )}
        />
      </div>

      <BroadcastersDrawerForm
        isOpen={broadcastersDrawer.isOpen}
        onClose={broadcastersDrawer.close}
        onSubmit={data => {
          const parenChannel = allChannels.find(channel => channel.id === data.parent_channel_id);

          // parent channel is required to create a child channel
          if (!parenChannel) {
            addErrorNotification(
              t(
                'No se ha podido guardar la emisora: la emisora madre seleccionada no está disponible'
              )
            );
            return;
          }

          const payloadData = {
            ...data,
            parent_channel_id: parenChannel.id,
            scope: data.scope ?? parenChannel.scope,
            country_code: data.country_code ?? parenChannel.country_code,
            media_type: data.media_type ?? parenChannel.media_type,
          };

          if (broadcastersDrawer.data?.type === FormTypeEnum.UPDATE) {
            updateChannel(
              {
                ...payloadData,
                id: broadcastersDrawer.data.data.id,
              },
              {
                onSuccess: () => {
                  broadcastersDrawer.close();
                },
              }
            );
          } else {
            createChannel(payloadData, {
              onSuccess: () => {
                broadcastersDrawer.close();
              },
            });
          }
        }}
        data={broadcastersDrawer.data || undefined}
        isMutating={isCreatingChannel || isUpdatingChannel}
      />

      <ModalDialog
        isOpen={broadcastersDialog.isOpen}
        onClose={broadcastersDialog.close}
        onAction={handleDeleteBroadcasters}
        heading={t('Eliminar emisora')}
        actionLabel={t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage title={t('¿Estás seguro de querer eliminar la emisora seleccionada?')} />
      </ModalDialog>
    </>
  );
}
